import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { apiService } from '../services/apiService';
import { Skill, Submission, SubmissionDetail } from '../types';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { FileUpload } from '../components/ui/FileUpload';
import { Trash2, Plus } from 'lucide-react';

export const SubmissionForm: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const { user } = useAuth();
    const navigate = useNavigate();
    const isEdit = !!id;

    const [periodType, setPeriodType] = useState<'Monthly' | 'Quarterly'>('Monthly');
    const [period, setPeriod] = useState('');
    const [details, setDetails] = useState<SubmissionDetail[]>([]);
    const [availableSkills, setAvailableSkills] = useState<Skill[]>([]);
    const [status, setStatus] = useState<'Draft' | 'Submitted' | 'Reviewed'>('Draft');

    useEffect(() => {
        apiService.getSkills().then(setAvailableSkills);
        if (isEdit && id) {
            apiService.getSubmissionById(id).then((sub) => {
                if (sub) {
                    setPeriodType(sub.periodType);
                    setPeriod(sub.period);
                    setDetails(sub.details);
                    setStatus(sub.status as any);
                }
            });
        } else {
            // Default period
            const now = new Date();
            setPeriod(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`);
        }
    }, [id, isEdit]);

    const handleAddSkill = () => {
        setDetails([...details, { skillId: availableSkills[0]?.id || '', selfScore: 1, achievementNotes: '' }]);
    };

    const handleRemoveSkill = (index: number) => {
        setDetails(details.filter((_, i) => i !== index));
    };

    const handleDetailChange = (index: number, field: keyof SubmissionDetail, value: any) => {
        const newDetails = [...details];
        newDetails[index] = { ...newDetails[index], [field]: value };
        setDetails(newDetails);
    };

    const fileToBase64 = (file: File): Promise<string> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = error => reject(error);
        });
    };

    const handleFilesSelected = async (index: number, files: File[]) => {
        const newDetails = [...details];
        const existingCertificates = newDetails[index].certificates || [];

        const newCertificates = await Promise.all(
            files.map(async (file) => ({
                name: file.name,
                size: file.size,
                type: file.type,
                dataUrl: await fileToBase64(file),
                uploadedAt: new Date().toISOString(),
            }))
        );

        newDetails[index] = {
            ...newDetails[index],
            certificates: [...existingCertificates, ...newCertificates],
        };
        setDetails(newDetails);
    };

    const handleRemoveFile = (skillIndex: number, fileIndex: number) => {
        const newDetails = [...details];
        const certificates = newDetails[skillIndex].certificates || [];
        newDetails[skillIndex] = {
            ...newDetails[skillIndex],
            certificates: certificates.filter((_, i) => i !== fileIndex),
        };
        setDetails(newDetails);
    };

    const [errors, setErrors] = useState<{ [key: string]: string }>({});

    const validateForm = () => {
        const newErrors: { [key: string]: string } = {};
        let isValid = true;

        // Period validation
        if (!period) {
            newErrors.period = 'Period is required';
            isValid = false;
        } else if (periodType === 'Monthly' && !/^\d{4}-\d{2}$/.test(period)) {
            newErrors.period = 'Format must be YYYY-MM';
            isValid = false;
        } else if (periodType === 'Quarterly' && !/^\d{4}-Q[1-4]$/.test(period)) {
            newErrors.period = 'Format must be YYYY-Qx (e.g., 2025-Q1)';
            isValid = false;
        }

        // Skills validation
        if (details.length === 0) {
            newErrors.skills = 'At least one skill is required';
            isValid = false;
        }

        // Check for duplicate skills
        const skillIds = details.map(d => d.skillId);
        const uniqueSkillIds = new Set(skillIds);
        if (skillIds.length !== uniqueSkillIds.size) {
            newErrors.skills = 'Duplicate skills are not allowed';
            isValid = false;
        }

        details.forEach((detail, index) => {
            if (!detail.skillId) {
                newErrors[`skill_${index}`] = 'Skill selection is required';
                isValid = false;
            }
            if (detail.selfScore < 1 || detail.selfScore > 10) {
                newErrors[`score_${index}`] = 'Score must be between 1 and 10';
                isValid = false;
            }
            if (!detail.achievementNotes || detail.achievementNotes.length < 10) {
                newErrors[`notes_${index}`] = 'Achievement notes must be at least 10 characters';
                isValid = false;
            }
        });

        setErrors(newErrors);
        return isValid;
    };

    const handleSubmit = async (newStatus: 'Draft' | 'Submitted') => {
        if (!user) return;

        // Only validate strictly for "Submitted" status, but basic checks for Draft
        if (newStatus === 'Submitted' && !validateForm()) {
            return;
        }

        const submissionData = {
            engineerId: user.id,
            engineerName: user.name,
            periodType,
            period,
            status: newStatus,
            details,
        };

        try {
            if (isEdit && id) {
                await apiService.updateSubmission(id, submissionData);
            } else {
                await apiService.createSubmission(submissionData);
            }
            navigate('/engineer/dashboard');
        } catch (error) {
            console.error('Failed to save submission', error);
        }
    };

    const isReadOnly = status === 'Reviewed';

    return (
        <div className="max-w-4xl mx-auto space-y-6">
            <div className="flex items-center justify-between">
                <h1 className="text-2xl font-bold text-gray-900">{isEdit ? 'Edit Submission' : 'New Submission'}</h1>
                {isReadOnly && (
                    <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
                        Read Only - Reviewed
                    </span>
                )}
            </div>

            {isReadOnly && (
                <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-r-md">
                    <div className="flex">
                        <div className="ml-3">
                            <p className="text-sm text-blue-700">
                                This submission has been reviewed by your manager and can no longer be edited.
                            </p>
                        </div>
                    </div>
                </div>
            )}

            <Card>
                <CardHeader>
                    <CardTitle>Period Details</CardTitle>
                </CardHeader>
                <CardContent className="grid gap-4 sm:grid-cols-2">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                        <select
                            className="w-full rounded-md border border-gray-300 p-2"
                            value={periodType}
                            onChange={(e) => setPeriodType(e.target.value as any)}
                            disabled={isEdit || isReadOnly}
                        >
                            <option value="Monthly">Monthly</option>
                            <option value="Quarterly">Quarterly</option>
                        </select>
                    </div>
                    <Input
                        label="Period (YYYY-MM or YYYY-Qx)"
                        value={period}
                        onChange={(e) => setPeriod(e.target.value)}
                        disabled={isReadOnly}
                        error={errors.period}
                    />
                </CardContent>
            </Card>

            <div className="space-y-4">
                {errors.skills && (
                    <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-r-md">
                        <div className="flex">
                            <div className="ml-3">
                                <p className="text-sm text-red-700">{errors.skills}</p>
                            </div>
                        </div>
                    </div>
                )}

                {details.map((detail, index) => (
                    <Card key={index} className={errors[`skill_${index}`] || errors[`score_${index}`] || errors[`notes_${index}`] ? 'border-red-300 ring-1 ring-red-300' : ''}>
                        <CardContent className="pt-6">
                            <div className="grid gap-4 sm:grid-cols-12">
                                <div className="sm:col-span-4">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Skill</label>
                                    <select
                                        className={`w-full rounded-md border p-2 ${errors[`skill_${index}`] ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300'}`}
                                        value={detail.skillId}
                                        onChange={(e) => handleDetailChange(index, 'skillId', e.target.value)}
                                        disabled={isReadOnly}
                                    >
                                        {availableSkills.map((s) => (
                                            <option key={s.id} value={s.id}>
                                                {s.name} ({s.category})
                                            </option>
                                        ))}
                                    </select>
                                    {errors[`skill_${index}`] && <p className="mt-1 text-xs text-red-600">{errors[`skill_${index}`]}</p>}
                                </div>
                                <div className="sm:col-span-2">
                                    <Input
                                        label="Score (1-10)"
                                        type="number"
                                        min="1"
                                        max="10"
                                        value={detail.selfScore}
                                        onChange={(e) => handleDetailChange(index, 'selfScore', parseInt(e.target.value))}
                                        error={errors[`score_${index}`]}
                                        disabled={isReadOnly}
                                    />
                                </div>
                                <div className="sm:col-span-5">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Achievements</label>
                                    <textarea
                                        className={`w-full rounded-md border p-2 text-sm ${errors[`notes_${index}`] ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300'}`}
                                        rows={2}
                                        value={detail.achievementNotes}
                                        onChange={(e) => handleDetailChange(index, 'achievementNotes', e.target.value)}
                                        disabled={isReadOnly}
                                    />
                                    {errors[`notes_${index}`] && <p className="mt-1 text-xs text-red-600">{errors[`notes_${index}`]}</p>}
                                </div>
                                {!isReadOnly && (
                                    <div className="sm:col-span-1 flex items-center justify-center pt-6">
                                        <Button variant="ghost" size="sm" onClick={() => handleRemoveSkill(index)} className="text-red-600 hover:text-red-700 hover:bg-red-50">
                                            <Trash2 className="h-4 w-4" />
                                        </Button>
                                    </div>
                                )}
                            </div>

                            {/* Certificate Upload Section */}
                            <div className="mt-4 pt-4 border-t border-gray-200">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Certificates / Supporting Documents
                                </label>
                                {isReadOnly ? (
                                    <div className="space-y-2">
                                        {detail.certificates?.map((cert, i) => (
                                            <div key={i} className="flex items-center text-sm text-gray-600">
                                                <span className="mr-2">📄</span>
                                                {cert.name}
                                            </div>
                                        ))}
                                        {(!detail.certificates || detail.certificates.length === 0) && (
                                            <span className="text-sm text-gray-500 italic">No certificates attached</span>
                                        )}
                                    </div>
                                ) : (
                                    <FileUpload
                                        onFilesSelected={(files) => handleFilesSelected(index, files)}
                                        existingFiles={detail.certificates || []}
                                        onRemoveFile={(fileIndex) => handleRemoveFile(index, fileIndex)}
                                        maxFiles={5}
                                        maxSizeMB={5}
                                    />
                                )}
                            </div>
                        </CardContent>
                    </Card>
                ))}
            </div>

            {!isReadOnly && (
                <div className="flex justify-center">
                    <Button variant="outline" onClick={handleAddSkill} className="w-full border-dashed">
                        <Plus className="mr-2 h-4 w-4" /> Add Skill
                    </Button>
                </div>
            )}

            <div className="flex justify-end space-x-4">
                <Button variant="secondary" onClick={() => navigate('/engineer/dashboard')}>
                    {isReadOnly ? 'Back to Dashboard' : 'Cancel'}
                </Button>
                {!isReadOnly && (
                    <>
                        <Button variant="outline" onClick={() => handleSubmit('Draft')}>
                            Save Draft
                        </Button>
                        <Button onClick={() => handleSubmit('Submitted')}>
                            Submit
                        </Button>
                    </>
                )}
            </div>
        </div>
    );
};
